import Point from 'ol/geom/Point';
import { Icon, Style } from 'ol/style';
import Stroke from 'ol/style/Stroke';

import { LineDecoration, LineSignatureType, StyleProperty } from 'cadenza/map/style/style-constants';
import { calculateRotation, getSize } from 'cadenza/map/style/style-util';
import { DEFAULT_OL_Z_INDEX } from 'cadenza/map/style/create-style';
import { createImageLine } from 'cadenza/map/style/create-image-line-style';
import { getColorWithOpacity } from 'cadenza/utils/colors/colors';
import { hashCode } from 'cadenza/utils/hash-code';
import { LRUCache } from 'cadenza/lru-cache';
import { GeometryType } from 'cadenza/map/geom/geometry-type';

const LINE_DASH_STYLES = {
  'Dash': [ 11, 6 ],
  'TinyDot': [ 1, 3 ],
  'Dot': [ 1, 7 ],
  'DashDot': [ 15, 9, 1, 9 ],
  'DashDotDot': [ 15, 9, 1, 9, 1, 9 ]
};

// We're trying to use the same algorithm as the one used on the backend DrawUtilities (drawArrow and calcArrowCoordinates)
const BASE_ARROW_WIDTH = 10;
const BASE_ARROW_HEIGHT = 12;
const BASE_CROSSBAR_WIDTH = 14;

const CANVAS_CACHE = new LRUCache();

export function createLineOlStyle (style) {
  const mainStyle = createLineMainStyle(style);
  const hasLineStartDecoration = style.lineStartDecoration && style.lineStartDecoration !== LineDecoration.NONE;
  const hasLineEndDecoration = style.lineEndDecoration && style.lineEndDecoration !== LineDecoration.NONE;
  if (!hasLineStartDecoration && !hasLineEndDecoration) {
    return mainStyle;
  } else {
    return (feature) => {
      const styles = [ mainStyle ];
      const mainStyleZIndex = mainStyle.getZIndex();

      const geometry = feature.getGeometry();
      if (geometry.getType() === GeometryType.LINE_STRING) {
        styles.push(...createLineDecorationStyles(geometry.getCoordinates(), style, mainStyleZIndex));
      } else if (geometry.getType() === GeometryType.MULTI_LINE_STRING) {
        geometry.getLineStrings().forEach(lineString => {
          styles.push(...createLineDecorationStyles(lineString.getCoordinates(), style, mainStyleZIndex));
        });
      } else {
        throw new Error(`Unsupported geometry of type: ${geometry.getType()}`);
      }
      return styles;
    };
  }
}

function createLineMainStyle (style) {
  const zIndex = style[StyleProperty.Z_INDEX] ?? DEFAULT_OL_Z_INDEX;
  if (style[StyleProperty.LINE_SIGNATURE_TYPE] === LineSignatureType.IMAGED) {
    const imageLineStyle = createImageLine(style);
    imageLineStyle.setZIndex(zIndex);
    return imageLineStyle;
  }

  return new Style({
    stroke: createStrokeForLine(style),
    zIndex
  });
}

function hasLineDecoration (style, lineDecorationPropertyName) {
  const isArrowLineDecoration = style[lineDecorationPropertyName] === LineDecoration.ARROW;
  const isFilledArrowLineDecoration = style[lineDecorationPropertyName] === LineDecoration.FILLED_ARROW;
  const isCrossBarLineDecoration = style[lineDecorationPropertyName] === LineDecoration.CROSSBAR;
  return isArrowLineDecoration || isFilledArrowLineDecoration || isCrossBarLineDecoration;
}

function createLineDecorationStyles (coordinates, style, mainStyleZIndex) {
  const styles = [];
  if (hasLineDecoration(style, StyleProperty.LINE_START_DECORATION)) {
    styles.push(createLineDecorationStyle(coordinates, style, StyleProperty.LINE_START_DECORATION, mainStyleZIndex));
  }

  if (hasLineDecoration(style, StyleProperty.LINE_END_DECORATION)) {
    styles.push(createLineDecorationStyle(coordinates, style, StyleProperty.LINE_END_DECORATION, mainStyleZIndex));
  }
  return styles;
}

export function createStrokeForLine ({
  [StyleProperty.LINE_WIDTH]: lineWidth,
  [StyleProperty.LINE_WIDTH_UNIT]: lineWidthUnit,
  [StyleProperty.FOREGROUND_COLOR]: foregroundColor,
  [StyleProperty.FOREGROUND_COLOR_OPACITY]: foregroundColorOpacity,
  [StyleProperty.LINE_STYLE]: lineStyle
}) {
  let stroke = null;
  if (lineWidth && lineWidth > 0) {
    stroke = new Stroke({
      color: getColorWithOpacity(foregroundColor, foregroundColorOpacity),
      width: getSize(lineWidth, lineWidthUnit)
    });
    // For line style 'Solid' the Map returns undefined,
    // thus OpenLayers uses the default which is 'Solid'.
    stroke.setLineDash(LINE_DASH_STYLES[lineStyle]);
  }
  return stroke;
}

function createLineDecorationStyle (coordinates, style, lineDecorationPropertyName, mainStyleZIndex) {
  const isFilledArrowLineDecoration = style[lineDecorationPropertyName] === LineDecoration.FILLED_ARROW;
  const isCrossBarLineDecoration = style[lineDecorationPropertyName] === LineDecoration.CROSSBAR;
  const { [StyleProperty.FOREGROUND_COLOR]: arrowColor, [StyleProperty.LINE_WIDTH]: arrowStrokeWidth } = style;
  const canvas = isCrossBarLineDecoration
    ? getCrossbarCanvas(arrowColor, arrowStrokeWidth)
    : getArrowCanvas(arrowColor, arrowStrokeWidth, isFilledArrowLineDecoration);
  const isLineStartDecoration = lineDecorationPropertyName === StyleProperty.LINE_START_DECORATION;
  const rotation = isLineStartDecoration
    ? calculateRotation(coordinates[1], coordinates[0]) // We want the angle offseted by 180 degrees, so invert the coords order
    : calculateRotation(coordinates[coordinates.length - 2], coordinates[coordinates.length - 1]);
  const geometry = new Point(isLineStartDecoration ? coordinates[0] : coordinates[coordinates.length - 1]);
  return createCanvasImageStyle(geometry, canvas, rotation, isCrossBarLineDecoration, mainStyleZIndex);
}

function createCanvasImageStyle (geometry, canvas, rotation, shouldCenterYIcon, mainStyleZIndex) {
  const olStartAndEndPointStyle = new Style({
    geometry: () => geometry,
    image: new Icon({
      anchor: [ 0.5, shouldCenterYIcon ? 0.5 : 0 ],
      img: canvas,
      imgSize: [ canvas.width, canvas.height ],
      rotation
    })
  });
  olStartAndEndPointStyle.setZIndex(mainStyleZIndex);
  return olStartAndEndPointStyle;
}

function getArrowCanvas (strokeColor, strokeWidth, shouldFillArrow) {
  const key = hashCode(JSON.stringify({ strokeColor, strokeWidth, shouldFillArrow }));
  return CANVAS_CACHE.getOrCreate(key, () => createArrowCanvas(strokeColor, strokeWidth, shouldFillArrow));
}

function createArrowCanvas (strokeColor, strokeWidth, shouldFillArrow) {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  const [ canvasWidth, canvasHeight ] = calculateArrowDimensions(strokeWidth);

  canvas.width = canvasWidth;
  canvas.height = canvasHeight;

  context.strokeStyle = strokeColor;
  context.fillStyle = strokeColor;
  context.lineWidth = strokeWidth;

  context.beginPath();
  context.moveTo(0, canvasHeight);
  context.lineTo(canvasWidth / 2, 0);
  context.lineTo(canvasWidth, canvasHeight);

  if (shouldFillArrow) {
    context.fill();
  } else {
    context.stroke();
  }

  return canvas;
}

function getCrossbarCanvas (strokeColor, strokeWidth) {
  const key = hashCode(JSON.stringify({ strokeColor, strokeWidth }));
  return CANVAS_CACHE.getOrCreate(key, () => createCrossbarCanvas(strokeColor, strokeWidth));
}

function createCrossbarCanvas (strokeColor, strokeWidth) {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  const [ canvasWidth, canvasHeight ] = calculateCrossbarDimensions(strokeWidth);

  canvas.width = canvasWidth;
  canvas.height = canvasHeight;

  context.strokeStyle = strokeColor;
  context.fillStyle = strokeColor;
  // Draw the line as thick as the whole canvas height
  context.lineWidth = canvasHeight;

  context.beginPath();
  // height/2 because we want the whole line to be visible, a 20px wide line from (0,0) to (width, 0), has 10px wide visibility
  context.moveTo(0, canvasHeight / 2);
  context.lineTo(canvasWidth, canvasHeight / 2);
  context.stroke();

  return canvas;
}

function calculateArrowDimensions (lineWidth) {
  const scaleFactor = getLineDecorationScale(lineWidth);
  return [ BASE_ARROW_WIDTH * scaleFactor, BASE_ARROW_HEIGHT * scaleFactor ];
}

function calculateCrossbarDimensions (lineWidth) {
  const scaleFactor = getLineDecorationScale(lineWidth);
  return [ BASE_CROSSBAR_WIDTH * scaleFactor, lineWidth ];
}

// Inspired by LineGeometryRenderer::getLineDecorationScale
function getLineDecorationScale (lineWidth) {
  return 1 + (lineWidth - 1) / 5;
}
